/**
 * The typed-name confirmation of a recursive REPLACE / REMOVE (D4) and of its Restart (D14), rendered INSIDE the surface
 * that asks (the Propagate dialog, the Transfers panel row) instead of the app-level confirm dialog: that dialog is a
 * separate Radix focus scope, and an open Radix Dialog / Sheet traps focus away from its input (observed in the e2e
 * run: the typed name never reached the field), so a confirmation that needs typing lives in the open surface itself.
 * Confirm stays disabled until the exact name is typed; Enter confirms once it matches; Cancel sends nothing.
 */
import { useId, useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';

export type TypedNameConfirmPanelProps = {
  title: string;
  lines: readonly string[];
  /** Background sentences behind a "How Jam SQL checks this change" disclosure (UI/UX fix round A, U28). */
  details?: readonly string[];
  name: string;
  confirmLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function TypedNameConfirmPanel({ title, lines, details, name, confirmLabel, pending = false, onConfirm, onCancel }: TypedNameConfirmPanelProps) {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const matches = typed === name;
  const confirm = () => { if (matches && !pending) onConfirm(); };
  return (
    <div className="grid gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" role="group" aria-label={title} data-testid="hns-typed-confirm">
      <p className="font-medium">{title}</p>
      <div className="grid gap-1 text-xs text-muted-foreground" data-testid="hns-typed-confirm-copy">{lines.map((line) => <span key={line}>{line}</span>)}</div>
      {details && details.length > 0 && (
        <details className="text-xs text-muted-foreground" data-testid="hns-typed-confirm-details">
          <summary className="cursor-pointer">How Jam SQL checks this change</summary>
          <ul className="mt-1 grid list-disc gap-1 pl-4">{details.map((line) => <li key={line}>{line}</li>)}</ul>
        </details>
      )}
      {/* U28: the name is quoted, so "Type raw to confirm" never reads as an instruction. */}
      <Label htmlFor={inputId}>Type <span className="font-mono">“{name}”</span> to confirm</Label>
      <Input
        id={inputId}
        value={typed}
        autoFocus
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => setTyped(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); confirm(); } }}
        data-testid="hns-typed-confirm-name"
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" disabled={pending} onClick={onCancel} data-testid="hns-typed-confirm-cancel">Cancel</Button>
        <Button type="button" variant="destructive" size="sm" disabled={!matches || pending} title={matches ? undefined : `Type “${name}” to enable`} onClick={confirm} data-testid="hns-typed-confirm-start">{confirmLabel}</Button>
      </div>
    </div>
  );
}
